import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../utils/i18n';
import { HELP_CONTENT, HelpSection } from './helpContent';
import { XIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon, HelpCircleIcon, BookOpenIcon } from 'lucide-react';
interface HelpPanelProps {
  isOpen: boolean;
  onClose: () => void;
}
export function HelpPanel({
  isOpen,
  onClose
}: HelpPanelProps) {
  const {
    t,
    locale
  } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['getting-started']));
  const content = HELP_CONTENT[locale] || HELP_CONTENT['en'];
  const toggleSection = (id: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSections(newExpanded);
  };
  // Filter content based on search
  const filteredContent = content.map(section => {
    // If search is empty, return section as is
    if (!searchQuery.trim()) return section;
    // Check if section title matches
    if (section.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return section;
    }
    // Check items
    const matchingItems = section.items.filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase()) || (Array.isArray(item.content) ? item.content.some(line => line.toLowerCase().includes(searchQuery.toLowerCase())) : item.content.toLowerCase().includes(searchQuery.toLowerCase())));
    if (matchingItems.length > 0) {
      return {
        ...section,
        items: matchingItems
      };
    }
    return null;
  }).filter(Boolean) as HelpSection[];
  // Auto-expand all if searching
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedSections(new Set(content.map(s => s.id)));
    }
  }, [searchQuery, content]);
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60]" onClick={onClose} data-id="element-502" />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[400px] lg:w-[480px] bg-white shadow-2xl z-[70] flex flex-col animate-in slide-in-from-right duration-300 border-l border-[#E8E8ED]" data-id="element-503">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E8ED] bg-white" data-id="element-504">
          <div className="flex items-center gap-2.5" data-id="element-505">
            <div className="w-8 h-8 rounded-lg bg-[#FC4C02]/10 flex items-center justify-center text-[#FC4C02]" data-id="element-506">
              <HelpCircleIcon className="w-5 h-5" data-id="element-507" />
            </div>
            <h2 className="text-lg font-bold text-[#2D2D2D]" data-id="element-508">
              {t('header.help')}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-[#9B9BA7] hover:text-[#2D2D2D] hover:bg-[#F7F7FA] rounded-lg transition-colors" data-id="element-509">
            <XIcon className="w-5 h-5" data-id="element-510" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-[#E8E8ED] bg-[#FAFAFA]" data-id="element-511">
          <div className="relative" data-id="element-512">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B9BA7]" data-id="element-513" />
            <input type="text" placeholder={t('settings.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-10 pl-9 pr-4 bg-white border border-[#E8E8ED] rounded-xl text-sm text-[#2D2D2D] placeholder:text-[#9B9BA7] focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent transition-all" data-id="element-514" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-id="element-515">
          {filteredContent.length === 0 ? <div className="text-center py-12" data-id="element-516">
              <BookOpenIcon className="w-12 h-12 text-[#E8E8ED] mx-auto mb-3" data-id="element-517" />
              <p className="text-[#9B9BA7] font-medium" data-id="element-518">
                {t('settings.noMatch')}
              </p>
            </div> : filteredContent.map(section => <div key={section.id} className="bg-white border border-[#E8E8ED] rounded-xl overflow-hidden shadow-sm" data-id="element-519">
                <button onClick={() => toggleSection(section.id)} className="w-full flex items-center justify-between px-4 py-3 bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors text-left" data-id="element-520">
                  <span className="font-bold text-[#2D2D2D] text-sm sm:text-base" data-id="element-521">
                    {section.title}
                  </span>
                  {expandedSections.has(section.id) ? <ChevronDownIcon className="w-5 h-5 text-[#9B9BA7]" data-id="element-522" /> : <ChevronRightIcon className="w-5 h-5 text-[#9B9BA7]" data-id="element-523" />}
                </button>

                {expandedSections.has(section.id) && <div className="divide-y divide-[#F0F0F5]" data-id="element-524">
                    {section.items.map((item, idx) => <div key={idx} className="p-4 space-y-2" data-id="element-525">
                        <h4 className="font-bold text-[#2D2D2D] text-sm flex items-start gap-2" data-id="element-526">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#FC4C02] mt-1.5 flex-shrink-0" data-id="element-527" />
                          {item.title}
                        </h4>
                        <div className="text-sm text-[#6B6B76] leading-relaxed pl-3.5" data-id="element-528">
                          {Array.isArray(item.content) ? <ul className="space-y-1.5" data-id="element-529">
                              {item.content.map((line, i) => <li key={i} data-id="element-530">{line}</li>)}
                            </ul> : <p data-id="element-531">{item.content}</p>}
                        </div>
                      </div>)}
                  </div>}
              </div>)}
        </div>
      </div>
    </>;
}